/**
 * User groups (Slack's "People → User groups", contracts userGroups.ts): handle rules, who may manage groups, the
 * People → User groups list, the group card and the "join the group's default channels" offer. Pure; shared by the
 * API (validation) and every client.
 *
 * Handles follow the username rules (logic/handles: 2–32 of a–z, 0–9, ".", "_", "-", letters or digits at the ends,
 * not reserved) and are unique across groups and member usernames, so `@handle` names one thing. The API enforces the
 * same checks (`groupHandleProblem`) and answers `handle_taken` / `invalid_request`.
 */
import type { Channel, Member, UserGroup, UserGroupPolicy } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { isExpired, isRestricted } from "./access.ts";
import { type HandleProblem, handleProblem, handleProblemMessage, normalizeHandle, slugifyHandle } from "./handles.ts";
import { fold, matchScore } from "./search.ts";

export const DEFAULT_GROUP_POLICY: UserGroupPolicy = "everyone";
export const GROUP_NAME_MAX = 80;
export const GROUP_DESCRIPTION_MAX = 250;

export type GroupFilter = "active" | "disabled";

/** Slack's default: every full member manages groups; `admins` restricts it to owners and admins. Guests never. */
export function canManageUserGroups(me: Member, policy: UserGroupPolicy | undefined, now: number): boolean {
  if (isExpired(me, now) || me.kind !== "human" || isRestricted(me)) return false;
  if ((policy ?? DEFAULT_GROUP_POLICY) === "everyone") return true;
  return me.role === "owner" || me.role === "admin";
}

/** Changing the policy itself: owners and admins. */
export function canSetGroupPolicy(me: Member, now: number): boolean {
  return !isExpired(me, now) && me.kind === "human" && !isRestricted(me) && (me.role === "owner" || me.role === "admin");
}

/** As typed → stored handle ("@Design Team" → "design.team" is the suggestion; the typed form is only normalized). */
export const normalizeGroupHandle = normalizeHandle;

/** The handle the create form proposes for a name ("Design Team" → "design-team"). */
export function suggestGroupHandle(name: string): string {
  return slugifyHandle(name).replaceAll(".", "-");
}

/**
 * Why `handle` (normalized) can't name a group: the username rules, or `taken` when another group (not
 * `exceptGroupId`) or a member's username has it.
 */
export function groupHandleProblem(handle: string, groups: UserGroup[], memberHandles: string[], exceptGroupId?: string): HandleProblem | undefined {
  const p = handleProblem(handle);
  if (p !== undefined) return p;
  if (groups.some((g) => g.id !== exceptGroupId && g.handle.toLowerCase() === handle)) return "taken";
  if (memberHandles.some((h) => h.toLowerCase() === handle)) return "taken";
  return undefined;
}

export type GroupFormProblem = "nameMissing" | "nameTooLong" | "descriptionTooLong" | HandleProblem;

/** The create / edit form's first problem, or undefined when it can be saved. */
export function groupFormProblem(
  form: { name: string; handle: string; description?: string },
  groups: UserGroup[],
  memberHandles: string[],
  exceptGroupId?: string,
): GroupFormProblem | undefined {
  const name = form.name.trim();
  if (name === "") return "nameMissing";
  if (name.length > GROUP_NAME_MAX) return "nameTooLong";
  const hp = groupHandleProblem(normalizeGroupHandle(form.handle), groups, memberHandles, exceptGroupId);
  if (hp !== undefined) return hp;
  if ((form.description ?? "").trim().length > GROUP_DESCRIPTION_MAX) return "descriptionTooLong";
  return undefined;
}

export function groupFormProblemMessage(p: GroupFormProblem): MessageDescriptor {
  switch (p) {
    case "nameMissing":
      return msg("userGroups.problem.nameMissing");
    case "nameTooLong":
      return msg("userGroups.problem.nameTooLong");
    case "descriptionTooLong":
      return msg("userGroups.problem.descriptionTooLong");
    case "taken":
      return msg("userGroups.problem.handleTaken");
    default:
      return handleProblemMessage(p);
  }
}

export function isGroupActive(g: UserGroup): boolean {
  return g.disabledAt === undefined;
}

/** Members of the group who are still active (deactivated people stay listed server-side but don't count). */
export function activeGroupMemberIds(g: UserGroup, members: Record<string, Member>): string[] {
  return g.memberIds.filter((id) => {
    const m = members[id];
    return m !== undefined && !m.deactivated;
  });
}

export type GroupRow = {
  id: string;
  name: string;
  /** "@design". */
  handle: string;
  description?: string;
  memberCount: number;
  disabled: boolean;
  /** I'm in it. */
  mine: boolean;
};

/**
 * People → User groups: the groups of one tab (active or disabled), matching `query` on name, handle or description,
 * sorted by name (then handle).
 */
export function groupRows(groups: UserGroup[], members: Record<string, Member>, meId: string, query: string, filter: GroupFilter): GroupRow[] {
  const q = query.trim();
  return groups
    .filter((g) => (filter === "active") === isGroupActive(g))
    .filter((g) => q === "" || matchScore(q, g.name) > 0 || matchScore(q, g.handle) > 0 || (g.description !== undefined && matchScore(q, g.description) >= 40))
    .toSorted(byName)
    .map((g) => {
      const row: GroupRow = {
        id: g.id,
        name: g.name,
        handle: `@${g.handle}`,
        memberCount: activeGroupMemberIds(g, members).length,
        disabled: !isGroupActive(g),
        mine: g.memberIds.includes(meId),
      };
      if (g.description !== undefined && g.description !== "") row.description = g.description;
      return row;
    });
}

function byName(a: UserGroup, b: UserGroup): number {
  const an = fold(a.name);
  const bn = fold(b.name);
  return an < bn ? -1 : an > bn ? 1 : a.handle < b.handle ? -1 : a.handle > b.handle ? 1 : 0;
}

/** Active groups a member belongs to, by name (their profile's "User groups"). */
export function groupsOf(userId: string, groups: UserGroup[]): UserGroup[] {
  return groups.filter((g) => isGroupActive(g) && g.memberIds.includes(userId)).toSorted(byName);
}

/** Is `userId` reached by a mention of this group (it is active and they're in it)? */
export function groupIncludes(g: UserGroup | undefined, userId: string): boolean {
  return g !== undefined && isGroupActive(g) && g.memberIds.includes(userId);
}

export type GroupCard = {
  id: string;
  name: string;
  handle: string;
  description?: string;
  disabled: boolean;
  /** Active members, by display name. */
  memberIds: string[];
  /** Default channels I can see, by name. */
  channelIds: string[];
  /** Default channels I can join now (visible, not archived, not joined): "Join N default channels". */
  joinableChannelIds: string[];
  canEdit: boolean;
};

/** The group's profile card (clicking a group mention or a row of the list). */
export function groupCard(
  g: UserGroup,
  ctx: { members: Record<string, Member>; channels: Record<string, Channel>; memberships: Record<string, unknown>; meId: string; policy?: UserGroupPolicy; now: number },
): GroupCard {
  const memberIds = activeGroupMemberIds(g, ctx.members).toSorted((a, b) => {
    const an = fold(ctx.members[a]!.displayName);
    const bn = fold(ctx.members[b]!.displayName);
    return an < bn ? -1 : an > bn ? 1 : a < b ? -1 : a > b ? 1 : 0;
  });
  const visible = g.channelIds.filter((id) => ctx.channels[id] !== undefined);
  const channelIds = visible.toSorted((a, b) => (ctx.channels[a]!.name < ctx.channels[b]!.name ? -1 : ctx.channels[a]!.name > ctx.channels[b]!.name ? 1 : 0));
  const me = ctx.members[ctx.meId];
  const card: GroupCard = {
    id: g.id,
    name: g.name,
    handle: `@${g.handle}`,
    disabled: !isGroupActive(g),
    memberIds,
    channelIds,
    joinableChannelIds: joinableGroupChannels(g, ctx.channels, ctx.memberships),
    canEdit: me !== undefined && canManageUserGroups(me, ctx.policy, ctx.now),
  };
  if (g.description !== undefined && g.description !== "") card.description = g.description;
  return card;
}

/** The group's default channels I could join: public, not archived, not mine yet (private ones need an invite). */
export function joinableGroupChannels(g: UserGroup, channels: Record<string, Channel>, memberships: Record<string, unknown>): string[] {
  return g.channelIds.filter((id) => {
    const c = channels[id];
    return c !== undefined && c.kind === "public" && !c.archived && memberships[id] === undefined;
  });
}

/**
 * Members added by an edit (`next` vs the group's `memberIds`): the people who join its default channels. Order of
 * `next` kept, no duplicates.
 */
export function addedMembers(before: string[], next: string[]): string[] {
  const had = new Set(before);
  const out: string[] = [];
  for (const id of next) if (!had.has(id) && !out.includes(id)) out.push(id);
  return out;
}

/** Group mention text as a chip or in plain text: "@design" (an unknown group: "@group"). */
export function groupMentionLabel(g: UserGroup | undefined): string {
  return g === undefined ? "@group" : `@${g.handle}`;
}
